

































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { VatsApi, Vat, GraphsApi } from '../../api';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import moment from 'moment';
import Toast from '../display/toast.vue';
import { ApiUtils } from '@/common/ApiUtils';
import { AxiosError } from 'axios';
import { filterTsAfter2010 } from '@/common/DateFilterUtil';

Vue.component('VueCtkDateTimePicker', VueCtkDateTimePicker);

@Component({
	components: {},
	props: {
		sensorId: Number,
		transceiverId: Number,
		series: String,
		hideSeries: {
			type: Array,
			default: () => {
				return [];
			},
		},
		productType: String,
		defaultDays: Number,
		emptyCapacitance: Number,
		connect: {
			type: Boolean,
			default: true,
		},
		useDayStartEnd: {
			type: Boolean,
			default: true,
		},
	},
})
export default class AdminChart extends Vue {
	public smallScreen = window.innerWidth;
	public visible = 'false';
	public graph: { [x: string]: any[] } | undefined;
	public sensorId: number | undefined;
	public transceiverId: number | undefined;
	public productType: string | undefined;
	public defaultDays: number | undefined;
	public emptyCapacitance: number | undefined;
	public connect: boolean;
	public useDayStartEnd: boolean;
	public series: string | undefined;
	public hideSeries: [];
	public height: number | undefined;
	public chart: am4charts.XYChart | undefined;
	public moment = moment;
	public format = 'DD-MM-YYYY HH:mm:ss'; // Use 24 hour time - 12hr gets mixed up at 0:00

	public offset = 0;
	public nDays = 7;

	private toast: Toast = new Toast();

	public form = {
		start: (this.useDayStartEnd ? moment().startOf('day') : moment())
			.subtract(this.defaultDays, 'days')
			.locale('en-nz')
			.format(this.format)
			.toString(),
		end: (this.useDayStartEnd ? moment().endOf('day') : moment())
			.locale('en-nz')
			.format(this.format)
			.toString(),
		shortcut: null,
	};

	public beforeDestroy() {
		if (this.chart) {
			this.chart.dispose();
		}
	}

	public myEventHandler(e: { target: { innerWidth: number } }) {
		this.smallScreen = e.target.innerWidth;
	}

	public created() {
		window.addEventListener('resize', this.myEventHandler);
		this.graphData();
	}

	public destroyed() {
		window.removeEventListener('resize', this.myEventHandler);
	}

	// offset = current date - end date(offset is how far back it will look)
	// nDays = end date - start date (nDays is how many days back from the offset)
	public updateRange(start: any, end: any) {
		const a = moment(end, this.format).local();
		const b = moment(start, this.format).local();
		const c = moment().endOf('day');
		this.offset = c.diff(a, 'days');
		this.nDays = a.diff(b, 'days') + 1;
	}

	public updateForm() {
		if (this.form.end !== null && this.form.start !== null) {
			this.updateRange(this.form.start, this.form.end);
			this.graphData();
		}
	}

	public async graphData() {
		if (this.form.end === null || this.form.start === null) {
			return;
		}
		try {
			const sensorId = this.sensorId;
			const transceiverId = this.transceiverId;
			const start = moment(this.form.start, this.format).format('X');
			const end = moment(this.form.end, this.format).format('X');
			const series = this.series;
			let { data } = {};
			if (transceiverId) {
				data = await new GraphsApi().graph('transceiver', transceiverId, start, end, series);
			} else if (sensorId) {
				data = await new GraphsApi().graph('sensor', sensorId, start, end, series);
			}
			this.graph = data.data.data;

			filterTsAfter2010(this.graph);

			await this.loadChart();
		} catch (e) {
			const error = e as AxiosError;
			if ((error.response?.data.errors ?? '') === 'Resource not found') {
				// The API throws a resource not found exception when there is no data found for a time range instead of returning an empty dataset.
				// Needs to be fixed there, but is causing trouble for people as there is no feedback here either. (silently fails and graph doesn't update)
				this.toast.makeToast('No data found for the given time range', 'No data', 'warning');
				this.graph = {};
				await this.loadChart();
				return;
			}
			const errorResponse = ApiUtils.handleApiError(
				e,
				'There was a problem loading the data for this time range',
			);
			this.toast.makeToast(errorResponse.errorMessage, 'Error', 'danger');
		}
	}

	public async loadChart() {
		if (this.chart) {
			await this.chart.dispose();
		}
		am4core.options.minPolylineStep = 5;
		// Create chart instance
		this.chart = am4core.create('adminChart' + this.sensorId + this.series, am4charts.XYChart);

		// Increase contrast by taking evey third color
		this.chart.colors.step = 3;
		this.chart.maskBullets = false;

		// Create axes
		const dateAxis = this.chart.xAxes.push(new am4charts.DateAxis());
		dateAxis.renderer.minGridDistance = 50;
		dateAxis.baseInterval = { timeUnit: 'minute', count: 5 }; // timeUnit: "minute", count: 5
		dateAxis.tooltipDateFormat = '[bold]dd-MM-yy, h:mm:ss a[/]';
		dateAxis.renderer.grid.template.location = 0;
		this.chart.scrollbarX = new am4core.Scrollbar();

		const startTs: number = parseInt(moment(this.form.start, this.format).format('X'));
		const endTs: number = parseInt(moment(this.form.end, this.format).format('X'));

		// Add legend and cursor
		this.chart.legend = new am4charts.Legend();
		this.chart.cursor = new am4charts.XYCursor();

		let maxTimestamp = -1;
		Object.keys(this.graph ?? {}).forEach((key, index) => {
			if (this.graph == undefined) return;
			this.createSeries(
				key,
				key,
				!(index % 2),
				'rectangle',
				2,
				dateAxis,
				undefined,
				this.graph[key].length > 0 && this.graph[key][0].rating != null,
			);
			for (let i = 0; i < this.graph[key].length; i++)
				maxTimestamp = Math.max(maxTimestamp, this.graph[key][i].timestamp);
		});

		if (maxTimestamp != -1) dateAxis.max = maxTimestamp;

		dateAxis.dateFormatter = new am4core.DateFormatter();
		dateAxis.dateFormatter.dateFormat = 'MM-dd';

		dateAxis.renderer.axisFills.template.disabled = false;
		dateAxis.renderer.axisFills.template.fill = am4core.color('#F6861F');
		dateAxis.renderer.axisFills.template.fillOpacity = 0.2;

		let dateFlag = true;
		let dayTemp = 0;
		dateAxis.fillRule = (dataItem: any) => {
			if (dayTemp !== dataItem.date.getDay()) {
				dayTemp = dataItem.date.getDay();
				dateFlag = !dateFlag;
			}
			dataItem.axisFill.visible = dateFlag;
		};

		if (!isNaN(startTs) && !isNaN(endTs)) {
			dateAxis.min = startTs * 1000;
			dateAxis.max = endTs * 1000;
		}
	}

	// Create series
	public createSeries(
		field: string,
		name: string,
		opposite: boolean,
		shape: string,
		strokeWidth: number,
		dateAxis: am4charts.DateAxis,
		lineColor?: string,
		isRating?: boolean,
	) {
		const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
		this.setValueAxis(valueAxis, field, isRating);
		const series = this.chart.series.push(new am4charts.LineSeries());
		series.dataFields.valueY = 'value';
		series.dataFields.dateX = 'timestamp';
		series.strokeWidth = strokeWidth || 0;
		series.yAxis = valueAxis;
		series.name = isRating ? 'rating' : name;
		series.tooltipText = '{name}: [bold]{valueY}[/]';

		if (!this.connect) {
			series.autoGapCount = dateAxis.baseInterval.count * 120;
		}
		series.connect = this.connect;

		if (this.hideSeries.includes(series.name)) {
			series.hide();
		}

		if (lineColor) {
			series.stroke = am4core.color(lineColor);
			series.fill = lineColor;
		}
		series.data = (isRating
			? this.graph[field].map(x => ({ ...x, value: x.rating }))
			: this.graph[field]
		).sort((a, b) => (a?.timestamp ?? 0) - (b?.timestamp ?? 0));
		valueAxis.renderer.line.strokeOpacity = 1;
		valueAxis.renderer.line.strokeWidth = 2;
		valueAxis.renderer.line.stroke = series.stroke;
		valueAxis.renderer.labels.template.fill = series.stroke;
		valueAxis.renderer.opposite = opposite;
		valueAxis.renderer.grid.template.disabled = true;
		if (this.emptyCapacitance) {
			this.addHorizontalLine(valueAxis, this.emptyCapacitance);
		}
	}

	private addHorizontalLine(
		valueAxis: am4charts.ValueAxis<am4charts.AxisRenderer>,
		yValue: number,
		colorHexCode: string = '#e00000',
	): void {
		const range = valueAxis.axisRanges.create();
		range.value = yValue;
		range.grid.stroke = am4core.color(colorHexCode);
		range.grid.strokeWidth = 2;
		range.grid.strokeOpacity = 1;
		range.grid.strokeDasharray = '0 3 30';
	}

	private setValueAxis(valueAxis, field, isRating = false) {
		switch (field) {
			case 'battery-voltage':
				valueAxis.strictMinMax = true;
				if (this.productType === 'milk') {
					valueAxis.max = 14000;
					valueAxis.min = 12000;
				} else if (this.productType === 'fuel') {
					valueAxis.max = 8000;
					valueAxis.min = 0;
				}
				break;
			case 'battery-solar':
				valueAxis.strictMinMax = true;
				valueAxis.max = 8000;
				valueAxis.min = 0;
				break;
			case 'mains-voltage':
				valueAxis.strictMinMax = true;
				valueAxis.max = 25000;
				valueAxis.min = 0;
				break;
			case 'remoteRssi':
			case 'localRssi':
				valueAxis.strictMinMax = true;
				valueAxis.max = 0;
				valueAxis.min = -140;
				break;
			case 'cell-strength':
				valueAxis.strictMinMax = true;
				if (this.productType === 'milk') {
					valueAxis.max = isRating ? 100 : -50;
					valueAxis.min = isRating ? 0 : -115;
				} else if (this.productType === 'fuel') {
					valueAxis.max = 140;
					valueAxis.min = 50;
				}
				break;
			case 'temperature':
			case 'magnetometer':
				break;
			case 'distance':
				valueAxis.min = 0;
				break;
			case 'voltage':
				valueAxis.strictMinMax = true;
				valueAxis.max = 4000;
				valueAxis.min = 0;
				break;
			case 'capacitance':
				valueAxis.strictMinMax = true;
				valueAxis.max =
					Math.max(
						...this.graph[field].map(function(o) {
							return o.value;
						}),
					) + 0.5;
				valueAxis.min =
					Math.min(
						this.emptyCapacitance,
						...this.graph[field].map(function(o) {
							return o.value;
						}),
					) - 0.5;
				break;
		}
	}
}
